import React from "react";
import {Autocomplete, Button, Stack, TextField} from "@mui/material";
import type {IndexedCategory, LevelSelection, TaxonomyIndex} from "~/prototypes/pages/taxonomy-canvas/taxonomyIndex.ts";
import {levelOptions, selectLevel} from "~/prototypes/pages/taxonomy-canvas/taxonomyIndex.ts";

type Props = {
    index: TaxonomyIndex
    /** Chosen category id per level, L1 first */
    selection: LevelSelection
    onChange: (selection: LevelSelection) => void
}
/**
 * One dropdown per level. A choice on an upper level narrows the levels below it; choosing a lower level
 * leaves the upper ones as they are.
 */
export const TaxonomyLevelFilter: React.FC<Props> = ({index, selection, onChange}) => {
    const parentLabel = (c: IndexedCategory) => c.parentId === null ? '' : index.byId.get(c.parentId)?.label ?? '';
    return <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{flexWrap: 'wrap', mb: 2}}>
        {index.levels.map((_, levelIdx) => {
            const options = levelOptions(index, levelIdx, selection);
            const chosen = selection[levelIdx];
            const value = (chosen === undefined ? undefined : index.byId.get(chosen)) ?? null;
            return <Autocomplete<IndexedCategory>
                key={levelIdx}
                size="small"
                sx={{width: 240}}
                options={options}
                value={value}
                // Always group below L1: MUI crashes if `groupBy` toggles while it still renders the old options
                groupBy={levelIdx > 0 ? parentLabel : undefined}
                getOptionLabel={c => c.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, c) => onChange(selectLevel(index, selection, levelIdx, c?.id))}
                renderInput={params => <TextField {...params} label={`L${levelIdx + 1}`} placeholder="All"/>}
            />;
        })}
        {selection.some(id => id !== undefined) && <Button size="small" onClick={() => onChange([])}>Clear</Button>}
    </Stack>
}
